import { BannerTypes, useOverlayBanner } from '@bimone/bimtrackreactcomponents';
import { useCallback, useEffect, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { useProjects } from '../../../hooks/useProjects';
import { Hub, Project } from '@bimone/bimtrack-core';
import { useTranslation } from 'react-i18next';
import { updateHub, updateProject } from '../../../redux/reducers/session';
import { useSession } from '../../../modules/session';

export interface UseNavBarRoutingProps {
  isNavBarRoutingEnabled?: boolean;
  isHubSelectionEnabled?: boolean;
  isProjectSelectionEnabled?: boolean;
  errorUrl?: string;
}

export interface UseNavBarRoutingResult {
  states: {
    isReady: boolean;
  };
  actions: {
    handleOnHubsSettled: (hubs: Hub[]) => void;
    handleOnProjectsSettled: (projects: Project[]) => void;
  };
}

enum QueryParamName {
  HUB = 'hubWithErrorDescription',
  PROJECT = 'projectWithErrorDescription',
}

export function useNavBarRouting({
  isNavBarRoutingEnabled = false,
  isHubSelectionEnabled = true,
  isProjectSelectionEnabled = true,
  errorUrl = '/',
}: UseNavBarRoutingProps): UseNavBarRoutingResult {
  const { hubName: hubNameFromParams, projectId: projectIdFromParams } = useParams();
  const { hub: currentHub, project: currentProject } = useSession();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    actions: { updateProjects },
  } = useProjects();
  const { addBannerFromProps } = useOverlayBanner();
  const { t: tNavBar } = useTranslation('navBar');
  const { t: tCommonErrors } = useTranslation('commonErrors');
  const [isInitialized, setIsInitialized] = useState(false);
  const isHubRoutingEnabled = isNavBarRoutingEnabled && isHubSelectionEnabled;
  const isProjectRoutingEnabled = isHubRoutingEnabled && isProjectSelectionEnabled;

  const resetHubAndProjectState = useCallback((): void => {
    batch(() => {
      dispatch(updateHub(undefined));
      dispatch(updateProjects(undefined));
      dispatch(updateProject(undefined));
    });
  }, [dispatch, updateProjects]);

  const handleError = useCallback(
    (parameter: QueryParamName): void => {
      addBannerFromProps({
        bannerTitle: tNavBar(parameter, { error: tCommonErrors('HTTP_404') }),
        bannerType: BannerTypes.Error,
      });

      resetHubAndProjectState();
      navigate(errorUrl);
    },
    [addBannerFromProps, errorUrl, navigate, resetHubAndProjectState, tCommonErrors, tNavBar]
  );

  const handleOnHubsSettled = useCallback(
    (hubs: Hub[]): void => {
      if (!isHubRoutingEnabled) {
        return;
      }

      const foundHub = hubs?.find(current => current.name.toLowerCase() === hubNameFromParams?.toLowerCase());

      if (hubNameFromParams && !foundHub) {
        handleError(QueryParamName.HUB);

        return;
      }

      if (foundHub === currentHub) {
        return;
      }

      dispatch(updateHub(foundHub));
    },
    [currentHub, dispatch, handleError, hubNameFromParams, isHubRoutingEnabled]
  );

  const handleOnProjectsSettled = useCallback(
    (projects: Project[]): void => {
      if (!isProjectRoutingEnabled) {
        return;
      }

      const isCurrentHubSameAsInPath = currentHub && currentHub.name.toLowerCase() === hubNameFromParams?.toLowerCase();

      if (!isCurrentHubSameAsInPath) {
        return;
      }

      const foundProject = projects.find(current => current.id === Number(projectIdFromParams));

      if (projectIdFromParams && !foundProject) {
        handleError(QueryParamName.PROJECT);

        return;
      }

      if (foundProject === currentProject) {
        return;
      }

      dispatch(updateProject(foundProject));
    },
    [currentHub, currentProject, dispatch, handleError, hubNameFromParams, isProjectRoutingEnabled, projectIdFromParams]
  );

  useEffect(() => {
    if (isInitialized) {
      return;
    }

    if (isNavBarRoutingEnabled) {
      resetHubAndProjectState();
    }

    setIsInitialized(true);
  }, [isInitialized, isNavBarRoutingEnabled, resetHubAndProjectState]);

  return {
    states: {
      isReady: isInitialized,
    },
    actions: {
      handleOnHubsSettled,
      handleOnProjectsSettled,
    },
  };
}
